
import { MenuItem } from '@/types/menu';
import { EmptyMenuItems } from '@/components/menu/EmptyMenuItems';
import { PublicMenuItemCard } from '@/components/menu/public/PublicMenuItemCard';
import { MenuItemWithCategory } from '@/hooks/usePublicMenu';
import { useTranslation } from 'react-i18next';

interface MenuItemListProps {
  items: MenuItemWithCategory[];
  onViewDetails: (item: MenuItem) => void;
  onAddToCart: (item: MenuItem, quantity?: number) => void;
  searchTerm: string;
  selectedCategory: string;
  selectedTags: string[];
}

export function MenuItemList({
  items,
  onViewDetails,
  onAddToCart,
  searchTerm,
  selectedCategory,
  selectedTags,
}: MenuItemListProps) {
  const { t } = useTranslation();

  // Filter items based on search, category and tags
  const filteredItems = items.filter((item) => {
    // First filter by availability
    if (!item.available) return false;

    // Then by search term
    if (
      searchTerm &&
      !item.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !item.description?.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return false;
    }

    // Then by category
    if (
      selectedCategory !== 'all' &&
      !item.categoryIds.includes(selectedCategory)
    ) {
      return false;
    }

    // Finally by tags
    if (
      selectedTags.length > 0 &&
      !selectedTags.every((tag) => item.tags?.includes(tag))
    ) {
      return false;
    }

    return true;
  });

  if (filteredItems.length === 0) {
    return (
      <EmptyMenuItems
        message={
          selectedTags.length > 0 || searchTerm || selectedCategory !== 'all'
            ? 'No items match your filters. Try adjusting your search or filters.'
            : 'No menu items available at this time.'
        }
      />
    );
  }

  return (
    <div className='grid gap-3 sm:gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'>
      {filteredItems.map((item) => (
        <PublicMenuItemCard
          key={item.id}
          item={item as MenuItem}
          onViewDetails={onViewDetails}
          onAddToCart={(item, quantity = 1) => onAddToCart(item, quantity)}
          showTags={true}
        />
      ))}
    </div>
  );
}
